import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiShoppingCart } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import useGetProducts from '../../../hooks/products/useGetProducts';
import useAddToCart from '../../../hooks/cart/useAddToCart';
import { PRODUCTS } from '../../../lib/routes';
import Loading from '../../Loading/Loading';

const ProductsPage = () => {
  // 🔍 Get category from URL: /products?category=figures
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');

  const { products, loading, error } = useGetProducts(category);
  const { cart, addToCart } = useAddToCart();

  const [addedProductId, setAddedProductId] = useState(null);
  const [/* clickedCartId */, setClickedCartId] = useState(null);
  const [quantities, setQuantities] = useState({});

  const handleQuantityChange = (id, delta, stock) => {
    setQuantities((prev) => {
      const newQty = Math.min(stock, Math.max(1, (prev[id] || 1) + delta));
      return { ...prev, [id]: newQty };
    });
  };

  const handleAddToCart = (product) => {
    const productSku = product.sku || product.id;
    const qty = quantities[product.id] || 1;
    const stockQty = product.quantity ?? 0;
    const existingQtyInCart =
      cart.find((item) => item.sku === productSku)?.quantity ?? 0;

    if (existingQtyInCart + qty > stockQty) {
      alert(`Cannot add more than ${stockQty} in stock.`);
      return;
    }

    addToCart({ ...product, quantity: qty });
    setAddedProductId(product.id);
    setClickedCartId(product.id);
    setTimeout(() => {
      setAddedProductId(null);
      setClickedCartId(null);
    }, 1200);
  };

  if (loading) return <Loading />;
  if (error)
    return (
      <p className="text-center mt-20 text-red-600 font-semibold px-4">
        Error loading products: {error.message}
      </p>
    );

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-10 sm:mb-12 text-purple-600 tracking-tight text-center">
          {category ? `${category} Collectibles` : 'Our Collectibles'}
        </h1>

        {products.length === 0 ? (
          <p className="text-center text-gray-500 text-base sm:text-lg px-4">
            No products found{category ? ` in "${category}"` : ''}.
          </p>
        ) : (
          <section
            aria-label="Product grid"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8"
          >
            {products.map(
              ({ id, name, price, imageUrl, featured, sku, quantity = 0 }) => {
                const productSku = sku || id;
                const cartItem = cart.find((item) => item.sku === productSku);
                const currentQty = quantities[id] || 1;

                return (
                  <article
                    key={id}
                    tabIndex={0}
                    className="relative border border-gray-200 rounded-lg p-4 flex flex-col items-center shadow-sm hover:shadow-lg transition h-[520px]"
                  >
                    {/* Add-to-Cart Animation */}
                    <AnimatePresence>
                      {addedProductId === id && (
                        <motion.div
                          key="added"
                          initial={{ scale: 0, opacity: 0, y: 10 }}
                          animate={{ scale: 1.5, opacity: 1, y: -10 }}
                          exit={{ scale: 0.5, opacity: 0, y: -40 }}
                          transition={{
                            duration: 0.6,
                            type: 'spring',
                            bounce: 0.6,
                          }}
                          className="absolute top-4 right-4 pointer-events-none text-3xl"
                        >
                          💖
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Product Image */}
                    <Link
                      to={`${PRODUCTS}/${id}`}
                      className="w-full h-64 overflow-hidden rounded-lg flex items-center justify-center bg-gray-50"
                    >
                      <img
                        src={
                          imageUrl ||
                          'https://via.placeholder.com/400x300?text=No+Image'
                        }
                        alt={name}
                        className="w-full h-full object-contain transition-transform duration-500 hover:scale-105"
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="text-center mt-4 flex-grow">
                      <p className="text-sm text-gray-500">{name}</p>
                      <h3 className="text-lg font-semibold mt-1 line-clamp-2">
                        {name}
                      </h3>
                      <p className="text-purple-600 font-bold mt-2">
                        ${price.toFixed(2)}
                      </p>
                    </div>

                    {/* Quantity Selector */}
                    <div className="flex items-center mt-4 space-x-2">
                      <button
                        onClick={() =>
                          handleQuantityChange(id, -1, quantity)
                        }
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                      >
                        -
                      </button>
                      <span className="px-4 py-1 border border-gray-300 rounded text-center">
                        {currentQty}
                      </span>
                      <button
                        onClick={() =>
                          handleQuantityChange(id, 1, quantity)
                        }
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100"
                      >
                        +
                      </button>
                    </div>

                    {/* Add to Cart Button */}
                    <button
                      onClick={() =>
                        handleAddToCart({
                          id,
                          sku: productSku,
                          name,
                          price,
                          imageUrl,
                          quantity,
                        })
                      }
                      className="mt-4 bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 transition w-full flex items-center justify-center space-x-2"
                    >
                      <FiShoppingCart className="w-5 h-5" />
                      <span>Add to Cart</span>
                    </button>

                    {/* In Cart Info */}
                    {cartItem && (
                      <motion.span
                        className="inline-block bg-green-700 bg-opacity-40 text-green-300 font-semibold text-xs sm:text-sm px-3 py-1 rounded-full border border-green-500 select-none mt-2"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        In Cart: {cartItem.quantity}
                      </motion.span>
                    )}
                  </article>
                );
              }
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
